import { useMemo } from 'react';
import { addBusinessDays } from 'date-fns';
import { ChantierMetrics } from './useChantierMetrics';

export interface TieredPenalty {
  minDays: number;
  maxDays: number;
  amountPerDay: number;
}

export interface SelectedMemberForDelay {
  id: string;
  prenom: string;
  nom: string;
  taux_horaire: number;
  coefficient_charges?: number; // Multiplicateur net → coût employeur (défaut 1.85)
  heures_par_jour?: number;
  // Infos équipe pour affichage pictogramme
  equipe_id?: string | null;
  equipe_pictogramme?: string | null;
  equipe_couleur?: string | null;
  // Info conflit (membre déjà affecté sur un autre chantier)
  hasConflict?: boolean;
  conflictChantierNom?: string;
}

export interface SimulationParams {
  metrics: ChantierMetrics | null;
  delayDays: number;
  penaltyType: 'percentage' | 'fixed' | 'tiered';
  penaltyValue: number;
  tieredRates?: TieredPenalty[];
  penaltyEnabled?: boolean;
  // Membres sélectionnés pour le retard (optionnel - si fourni, remplace cout_journalier_equipe)
  selectedMembersForDelay?: SelectedMemberForDelay[];
}

export interface SimulationResult {
  penaltyAmount: number;
  additionalLaborCost: number;
  totalAdditionalCost: number;
  finalMarginWithDelay: number;
  finalProfitabilityWithDelay: number;
  profitabilityChange: number;
  criticalDelayDays: number;
  recommendations: string[];
  newEndDate: Date | null;
  contractEndDate: Date | null;
}

const calculateTieredPenalty = (days: number, rates: TieredPenalty[]): number => {
  let total = 0;
  
  for (const tier of rates) {
    if (days <= 0) break;
    
    const applicableDays = Math.min(
      Math.max(0, days - tier.minDays + 1),
      tier.maxDays - tier.minDays + 1
    );
    
    if (applicableDays > 0) {
      total += applicableDays * tier.amountPerDay;
    }
  }
  
  return total;
};

const findCriticalDay = (
  availableMargin: number, // Marge disponible (budget - coûts engagés)
  budgetHt: number,
  dailyTeamCost: number,
  penaltyType: 'percentage' | 'fixed' | 'tiered',
  penaltyValue: number,
  tieredRates?: TieredPenalty[]
): number => {
  // Si pas de marge disponible ou coût journalier nul, pas de calcul possible
  if (availableMargin <= 0) return 0;
  if (dailyTeamCost <= 0 && penaltyValue <= 0) return 100;

  // Chercher le jour où la marge devient négative
  for (let d = 1; d <= 100; d++) {
    let penalty = 0;

    switch (penaltyType) {
      case 'percentage':
        penalty = (budgetHt * penaltyValue / 100) * d;
        break;
      case 'fixed':
        penalty = penaltyValue * d;
        break;
      case 'tiered':
        penalty = tieredRates ? calculateTieredPenalty(d, tieredRates) : 0;
        break;
    }

    const additionalLabor = dailyTeamCost * d;
    const totalCostForDelay = penalty + additionalLabor;

    // La marge disponible moins les coûts du retard
    if (totalCostForDelay >= availableMargin) {
      return Math.max(0, d - 1);
    }
  }

  return 100; // Plus de 100 jours
};

const generateRecommendations = (
  profitabilityWithDelay: number,
  criticalDays: number,
  delayDays: number,
  penaltyAmount: number
): string[] => {
  const recommendations: string[] = [];
  
  if (profitabilityWithDelay < 0) {
    recommendations.push('🚨 URGENT : Le projet est en déficit. Actions correctives immédiates requises.');
    recommendations.push('Envisager une renégociation du contrat ou accélération drastique.');
  } else if (profitabilityWithDelay < 5) {
    recommendations.push('⚠️ ALERTE : Rentabilité très faible. Risque de déficit imminent.');
    recommendations.push('Mettre en place un plan d\'accélération pour rattraper le retard.');
  } else if (profitabilityWithDelay < 10) {
    recommendations.push('⚠️ ATTENTION : Rentabilité en zone critique.');
    recommendations.push('Surveiller de près l\'avancement et prévoir des mesures préventives.');
  }
  
  if (delayDays >= criticalDays - 2 && criticalDays < 100) {
    recommendations.push(`⏰ Point critique à ${criticalDays} jours de retard. Vous êtes très proche !`);
  } else if (criticalDays < 100) {
    recommendations.push(`📊 Point critique estimé à ${criticalDays} jours de retard.`);
  }
  
  if (penaltyAmount > 1000) {
    recommendations.push(`💰 Pénalités importantes (${penaltyAmount.toFixed(0)}€). Prévoir une provision budgétaire.`);
  }
  
  if (recommendations.length === 0) {
    recommendations.push('✅ Le projet reste rentable malgré le retard simulé.');
    recommendations.push('💡 Continuer à surveiller l\'avancement pour éviter les dépassements.');
  }
  
  return recommendations;
};

// Calcul du coût journalier pour une liste de membres
const calculateDailyTeamCost = (members: SelectedMemberForDelay[]): number => {
  return members.reduce((total, m) => {
    const heures = m.heures_par_jour || 8;
    const coutJournalier = m.taux_horaire * (m.coefficient_charges || 1.85) * heures;
    return total + coutJournalier;
  }, 0);
};

export function useDelayPenaltySimulation(params: SimulationParams): SimulationResult {
  return useMemo(() => {
    const { metrics, delayDays, penaltyType, penaltyValue, tieredRates, penaltyEnabled = true, selectedMembersForDelay } = params;

    // Valeurs par défaut si pas de métriques
    if (!metrics) {
      return {
        penaltyAmount: 0,
        additionalLaborCost: 0,
        totalAdditionalCost: 0,
        finalMarginWithDelay: 0,
        finalProfitabilityWithDelay: 0,
        profitabilityChange: 0,
        criticalDelayDays: 0,
        recommendations: ['Aucune métrique disponible pour simuler'],
        newEndDate: null,
        contractEndDate: null,
      };
    }

    // Coût journalier : utiliser les membres sélectionnés si fournis, sinon cout_journalier_equipe par défaut
    const dailyTeamCost = selectedMembersForDelay && selectedMembersForDelay.length > 0
      ? calculateDailyTeamCost(selectedMembersForDelay)
      : metrics.cout_journalier_equipe;

    // Marge de référence (SANS retard existant)
    // metrics.marge_finale inclut déjà le cout_retard, on doit l'exclure pour avoir la marge de base
    const coutRetardExistant = metrics.cout_retard || 0;
    const margeSansRetard = metrics.marge_finale + coutRetardExistant;
    const rentabiliteSansRetard = metrics.budget_ht > 0
      ? (margeSansRetard / metrics.budget_ht) * 100
      : 0;

    // 1. Calcul de la pénalité selon le type (SEULEMENT si penaltyEnabled)
    let penaltyAmount = 0;
    if (penaltyEnabled) {
      switch (penaltyType) {
        case 'percentage':
          penaltyAmount = (metrics.budget_ht * penaltyValue / 100) * delayDays;
          break;
        case 'fixed':
          penaltyAmount = penaltyValue * delayDays;
          break;
        case 'tiered':
          penaltyAmount = tieredRates ? calculateTieredPenalty(delayDays, tieredRates) : 0;
          break;
      }
    }

    // 2. Coût additionnel de main d'œuvre (utilise dailyTeamCost calculé plus haut)
    const additionalLaborCost = dailyTeamCost * delayDays;

    // 3. Coût total additionnel
    const totalAdditionalCost = penaltyAmount + additionalLaborCost;

    // 4. Impact sur la marge finale (basé sur la marge SANS retard existant)
    const finalMarginWithDelay = margeSansRetard - totalAdditionalCost;
    const finalProfitabilityWithDelay = metrics.budget_ht > 0
      ? (finalMarginWithDelay / metrics.budget_ht) * 100
      : 0;

    // 5. Changement de rentabilité (par rapport à la marge SANS retard)
    const profitabilityChange = finalProfitabilityWithDelay - rentabiliteSansRetard;

    // 6. Calcul du point critique (utilise la marge SANS retard comme base)
    const criticalDelayDays = findCriticalDay(
      margeSansRetard,
      metrics.budget_ht,
      dailyTeamCost,
      penaltyEnabled ? penaltyType : 'fixed',
      penaltyEnabled ? penaltyValue : 0,
      penaltyEnabled ? tieredRates : undefined
    );
    
    // 7. Dates
    // duree_estimee_jours = jours ouvrés → utiliser addBusinessDays (pas addDays)
    const contractEndDate = metrics.date_debut && metrics.duree_estimee_jours
      ? addBusinessDays(new Date(metrics.date_debut), metrics.duree_estimee_jours)
      : null;
    
    // Pour la nouvelle date de fin, on utilise les jours OUVRÉS (pas les week-ends)
    // 1.5 jours ouvrés = on arrondit au jour supérieur = 2 jours ouvrés
    // Exemple: vendredi 02/01 + 2 jours ouvrés = mardi 06/01 (on saute le week-end)
    const newEndDate = contractEndDate
      ? addBusinessDays(contractEndDate, Math.ceil(delayDays))
      : null;
    
    // 8. Recommandations
    const recommendations = generateRecommendations(
      finalProfitabilityWithDelay,
      criticalDelayDays,
      delayDays,
      penaltyAmount
    );
    
    return {
      penaltyAmount,
      additionalLaborCost,
      totalAdditionalCost,
      finalMarginWithDelay,
      finalProfitabilityWithDelay,
      profitabilityChange,
      criticalDelayDays,
      recommendations,
      newEndDate,
      contractEndDate,
    };
  }, [params.metrics, params.delayDays, params.penaltyType, params.penaltyValue, params.tieredRates, params.penaltyEnabled, params.selectedMembersForDelay]);
}
